import { useCallback, useContext, useRef } from 'react';
import styled from '@emotion/styled';
import type { QuantAnalysisItem } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import {
  SetSelectedQuantAnalysisItemContext,
  SelectedQuantAnalysisItemContext,
} from '@trinity/chat-components';
import { QuantAnalysesPanelItem } from '@/screens/Project.QuantAnalysis/AnalysesList.Item';
import { CreateFolderInput } from '../CreateFolder';
import styles from '../style/SearchDropdown.module.css';
import { SearchFolderItem } from './SearchFolderItem';

type SearchResult = {
  folderId: string;
  item: { id: number; name: string };
  isMatch: boolean;
};

type FolderSearchResult = {
  folderId: string;
  folderName: string;
  items: SearchResult[];
  hasMatches: boolean;
  shouldExpand: boolean;
};

type SearchDropdownProps = {
  isOpen: boolean;
  filteredOptions: QuantAnalysisItem[];
  folderResults: FolderSearchResult[];
  searchValue: string;
  noOptionsMessage?: string;
  onOptionSelect?: (option: QuantAnalysisItem) => void;
  maxMenuHeight?: number;
  minMenuHeight?: number;
};

export const SearchDropdown = ({
  isOpen,
  filteredOptions,
  folderResults,
  searchValue,
  noOptionsMessage,
  onOptionSelect,
  maxMenuHeight,
  minMenuHeight,
}: SearchDropdownProps) => {
  const setSelectedItemId = useContext(SetSelectedQuantAnalysisItemContext);
  const selectedItemId = useContext(SelectedQuantAnalysisItemContext);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOptionSelect = useCallback((option: QuantAnalysisItem) => {
    setSelectedItemId(option.id);
    onOptionSelect?.(option);
  }, [setSelectedItemId, onOptionSelect]);

  const handleItemSelect = useCallback((itemId: number) => {
    setSelectedItemId(itemId);

    const matchedOption = filteredOptions.find(opt => opt.id === itemId);
    if (matchedOption) {
      onOptionSelect?.(matchedOption);
    }
  }, [setSelectedItemId, onOptionSelect, filteredOptions]);

  const handleNoOptionsMessage = useCallback(() => {
    if (!searchValue?.length) return null;
    return noOptionsMessage || 'No analysis tabs match your search';
  }, [searchValue, noOptionsMessage]);

  if (!isOpen) {
    return null;
  }

  const shouldShowNoOptions = searchValue?.length > 0 &&
    filteredOptions.length === 0 &&
    !folderResults.some(folder => folder.hasMatches);
  const noOptionsText = handleNoOptionsMessage();

  return (
    <div
      ref={dropdownRef}
      className={styles.dropdown}>
      <MenuList
        maxMenuHeight={maxMenuHeight}
        minMenuHeight={minMenuHeight}>

        <CreateFolderInput />

        {/* Folder structure with search results */}
        <div className={styles.foldersList}>
          {folderResults.map(folder => (
            <SearchFolderItem
              key={folder.folderId}
              folderId={folder.folderId}
              folderName={folder.folderName}
              items={folder.items}
              shouldExpand={folder.shouldExpand}
              onItemSelect={handleItemSelect}
              selectedItemId={selectedItemId}
              searchValue={searchValue} />
          ))}
        </div>

        {/* Filter out any already present in the folders for quant analysis items on the root-level */}
        <div className={styles.optionsList}>
          {filteredOptions
            .filter(option => {
              const isInFolder = folderResults.some(folder =>
                folder.items.some(item => item.item.id === option.id),
              );
              return !isInFolder;
            })
            .map(option => {
              const isSelected = option.id === selectedItemId;
              return (
                <div key={option.id}>
                  <QuantAnalysesPanelItem
                    name={option.name}
                    selected={isSelected}
                    onSelect={() => handleOptionSelect(option)}
                  />
                </div>
              );
            })}
        </div>

        {/* No options message */}
        {shouldShowNoOptions && noOptionsText && (
          <div className={styles.noOptionsMessage}>
            {noOptionsText}
          </div>
        )}
      </MenuList>
    </div>
  );
};

type MenuListProps = {
  maxMenuHeight?: number;
  minMenuHeight?: number;
};

const MenuList = styled.div<MenuListProps>(({ maxMenuHeight, minMenuHeight }) => ({
  maxHeight: maxMenuHeight ? `${maxMenuHeight}px` : undefined,
  minHeight: minMenuHeight ? `${minMenuHeight}px` : undefined,
  overflowY: 'auto',
}));

SearchDropdown.displayName = 'SearchDropdown';
